import { Download, Table2 } from "lucide-react";

import { type Column } from "@fawkes/ui";

import { IndeelbareTabel } from "../components/IndeelbareTabel";
import type { RecenteOpname } from "../api/insights";

/**
 * De laatste opnames als tabel, onderaan het scherm.
 *
 * <p>Dit is de enige plek op dit scherm waar losse records staan in plaats van cijfers. Dat is met
 * opzet: een grafiek zegt hoeveel, deze tabel zegt wie — en zonder dat laatste is een getal niet na
 * te lopen.
 *
 * <p><b>Een leeg vakje blijft leeg.</b> Geen streepje, geen "onbekend", geen nul: een dier zonder
 * geboortedatum heeft geen leeftijd, en die drie manieren om dat op te schrijven zeggen elk iets
 * anders dan "niet ingevuld".
 */
export function RecenteOpnames({ opnames, onExport, t }: {
  opnames: RecenteOpname[];
  /** Exporteren; weglaten laat de knop weg in plaats van hem niets te laten doen. */
  onExport?: () => void;
  t: (key: string) => string;
}) {
  if (opnames.length === 0) return null;

  const datum = new Intl.DateTimeFormat("nl-NL",
    { day: "numeric", month: "short", year: "numeric" });

  const kolommen: Column<RecenteOpname>[] = [
    { key: "registratie", header: t("dataInsight.kolomRegistratie"), cell: (o) => o.registratienummer ?? "" },
    { key: "diersoort", header: t("dataInsight.diersoort"), cell: (o) => o.diersoort ?? "" },
    { key: "ras", header: t("dataInsight.kolomRas"), cell: (o) => o.ras ?? "" },
    { key: "geslacht", header: t("dataInsight.kolomGeslacht"), cell: (o) => o.geslacht ?? "" },
    {
      // Nul jaar is een leeftijd en hoort er te staan; niets ingevuld blijft leeg.
      key: "leeftijd",
      header: t("dataInsight.leeftijd"),
      cell: (o) => (o.leeftijd == null ? "" : o.leeftijd === 1
        ? `1 ${t("dataInsight.jaar")}`
        : `${o.leeftijd} ${t("dataInsight.jaren")}`),
    },
    { key: "herkomst", header: t("dataInsight.kolomHerkomst"), cell: (o) => o.herkomst ?? "" },
    {
      key: "datum",
      header: t("dataInsight.kolomDatum"),
      cell: (o) => (o.datum ? datum.format(new Date(o.datum + "T00:00:00")) : ""),
    },
  ];

  return (
    <section className="fawkes-samenvatting fawkes-di__opnames">
      <div className="fawkes-samenvatting__kop">
        <span className="fawkes-samenvatting__kopicoon" aria-hidden="true"><Table2 /></span>
        <div className="fawkes-samenvatting__koptekst">
          <h3 className="fawkes-samenvatting__titel">{t("dataInsight.recenteKop")}</h3>
          <p className="fawkes-samenvatting__assen">
            {t("dataInsight.recenteSub").replace("{n}", String(opnames.length))}
          </p>
        </div>
        {onExport ? (
          <div className="fawkes-samenvatting__acties">
            <button type="button" className="fawkes-samenvatting__knop" onClick={onExport}>
              <Download aria-hidden="true" />
              {t("zoekresultaten.samenvatting.exportExcel")}
            </button>
          </div>
        ) : null}
      </div>

      <IndeelbareTabel<RecenteOpname>
        tabelKey="data-insight-recente-opnames"
        columns={kolommen}
        rows={opnames}
        getRowKey={(o) => (o.registratienummer ?? "") + o.datum}
        empty=""
      />
    </section>
  );
}
